import { useEffect, useState } from 'react';

/**
 * Light/dark theme. The active theme is an explicit `data-theme` on <html>
 * (set by `initTheme`); CSS in colors_and_type.css keys the dark palette off it.
 * Precedence: the user's saved choice, else the OS `prefers-color-scheme`.
 */
export type Theme = 'light' | 'dark';

const KEY = 'he-theme';
const mql = () => window.matchMedia('(prefers-color-scheme: dark)');

/** The theme to show: saved choice if any, otherwise the OS preference. */
export function resolvedTheme(): Theme {
  const saved = localStorage.getItem(KEY);
  if (saved === 'light' || saved === 'dark') return saved;
  return mql().matches ? 'dark' : 'light';
}

function apply(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
}

/** The currently applied theme (falls back to resolving if not yet applied). */
export function getTheme(): Theme {
  const t = document.documentElement.dataset.theme;
  return t === 'light' || t === 'dark' ? t : resolvedTheme();
}

/** Persist + apply an explicit theme, notifying `useTheme` subscribers. */
export function setTheme(theme: Theme): void {
  localStorage.setItem(KEY, theme);
  apply(theme);
  window.dispatchEvent(new CustomEvent('he-theme'));
}

/** Toggle light↔dark; returns the new theme. */
export function toggleTheme(): Theme {
  const next: Theme = getTheme() === 'dark' ? 'light' : 'dark';
  setTheme(next);
  return next;
}

/**
 * Apply the theme on boot (call before first paint to avoid a flash), and keep
 * following the OS preference until the user makes an explicit choice.
 */
export function initTheme(): void {
  apply(resolvedTheme());
  mql().addEventListener('change', () => {
    if (!localStorage.getItem(KEY)) apply(resolvedTheme());
  });
}

/** React binding: current theme + a toggle, re-rendering on any theme change. */
export function useTheme(): [Theme, () => void] {
  const [theme, setThemeState] = useState<Theme>(getTheme);
  useEffect(() => {
    const sync = () => setThemeState(getTheme());
    window.addEventListener('he-theme', sync);
    const mq = mql();
    mq.addEventListener('change', sync);
    return () => {
      window.removeEventListener('he-theme', sync);
      mq.removeEventListener('change', sync);
    };
  }, []);
  return [theme, toggleTheme];
}
